import { Statuses } from '../enums/status';
import { Status } from '../enums/status';
import { id } from '@monorepo/tools/src/lib/types/primitives';

export type WeightedDestinationLink = {
	status?: Status;
	weight?: number;
};

export type AllocatableDestinationLink = WeightedDestinationLink & {
	getCatalogDestinationId(): id | undefined;
	id?: id;
	setWeight(weight: number): void;
};

const PERCENTAGE_TOTAL = 100;
const PERCENTAGE_TOLERANCE = 0.001;

export function isDestinationLinkActive(link: WeightedDestinationLink): boolean {
	return link.status === Statuses.Active;
}

export function getLinkWeight(link: WeightedDestinationLink): number {
	const weight = link.weight;
	if (weight == null || Number.isNaN(Number(weight)) || Number(weight) < 0) {
		return 0;
	}
	return Number(weight);
}

export function sumActiveWeights(links: WeightedDestinationLink[]): number {
	return links.reduce((sum, link) => {
		if (!isDestinationLinkActive(link)) {
			return sum;
		}
		return sum + getLinkWeight(link);
	}, 0);
}

export function getTrafficPercentage(link: WeightedDestinationLink, links: WeightedDestinationLink[]): number {
	if (!isDestinationLinkActive(link)) {
		return 0;
	}
	const total = sumActiveWeights(links);
	if (total <= 0) {
		return 0;
	}
	return (getLinkWeight(link) / total) * 100;
}

export function formatTrafficPercentage(link: WeightedDestinationLink, links: WeightedDestinationLink[]): string {
	if (!isDestinationLinkActive(link)) {
		return '—';
	}
	const total = sumActiveWeights(links);
	if (total <= 0) {
		return '—';
	}
	const pct = getTrafficPercentage(link, links);
	return `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(1)}%`;
}

export function hasPausedDestination(links: WeightedDestinationLink[]): boolean {
	return links.some(link => !isDestinationLinkActive(link));
}

export function getDestinationAllocationKey(link: AllocatableDestinationLink): string {
	return String(link.getCatalogDestinationId() ?? link.id ?? '');
}

export function getDesiredPercentageDefaults(links: AllocatableDestinationLink[]): Record<string, string> {
	const total = sumActiveWeights(links);
	const defaults: Record<string, string> = {};
	for (const link of links) {
		if (!isDestinationLinkActive(link)) {
			continue;
		}
		const key = getDestinationAllocationKey(link);
		if (!key) {
			continue;
		}
		if (total <= 0) {
			defaults[key] = '';
			continue;
		}
		const pct = getTrafficPercentage(link, links);
		defaults[key] = pct.toFixed(0);
	}
	return defaults;
}

export function parseDesiredPercentage(raw: string | undefined): number {
	let value = Number(raw);
	if (value <= 0) {
		value = 1;
	}
	return Number.isFinite(value) && value >= 0 ? value : 0;
}

export function sumDesiredPercentages(links: AllocatableDestinationLink[], drafts: Record<string, string>): number {
	return links.reduce((sum, link) => {
		if (!isDestinationLinkActive(link)) {
			return sum;
		}
		const key = getDestinationAllocationKey(link);
		return sum + parseDesiredPercentage(drafts[key]);
	}, 0);
}

export function areDesiredPercentagesValid(sum: number, target = PERCENTAGE_TOTAL): boolean {
	return Math.abs(sum - target) <= PERCENTAGE_TOLERANCE;
}

export function applyDesiredPercentagesToWeights(links: AllocatableDestinationLink[], drafts: Record<string, string>): void {
	for (const link of links) {
		if (!isDestinationLinkActive(link)) {
			continue;
		}
		const key = getDestinationAllocationKey(link);
		const pct = parseDesiredPercentage(drafts[key]);
		link.setWeight(Math.max(1, Math.round(pct)));
	}
}
